import { useState, useEffect, createContext, useContext, useCallback } from "react";
import { FaCheckCircle, FaExclamationCircle, FaInfoCircle, FaTimes, FaBolt } from "react-icons/fa";

type ToastType = "success" | "error" | "info" | "automation";

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType, duration?: number) => void;
  showAutomationToast: (triggered: number, errors: string[]) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback((message: string, type: ToastType = "info", duration = 4000) => {
    const id = Math.random().toString(36).substring(7);
    setToasts((prev) => [...prev, { id, message, type, duration }]);
  }, []);

  const showAutomationToast = useCallback((triggered: number, errors: string[]) => {
    if (triggered > 0) {
      showToast(
        `⚡ ${triggered} automation${triggered > 1 ? "s" : ""} triggered`,
        "automation",
        3000
      );
    }
    if (errors.length > 0) {
      errors.forEach((error) => {
        showToast(`Automation error: ${error}`, "error", 6000);
      });
    }
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, showAutomationToast }}>
      {children}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, onClose }: { toast: Toast; onClose: () => void }) {
  useEffect(() => {
    if (toast.duration) {
      const timer = setTimeout(onClose, toast.duration);
      return () => clearTimeout(timer);
    }
  }, [toast.duration, onClose]);

  const icons = {
    success: <FaCheckCircle className="text-green-500" />,
    error: <FaExclamationCircle className="text-red-500" />,
    info: <FaInfoCircle className="text-blue-500" />,
    automation: <FaBolt className="text-yellow-500" />,
  };

  const bgColors = {
    success: "bg-green-50 border-green-200",
    error: "bg-red-50 border-red-200",
    info: "bg-blue-50 border-blue-200",
    automation: "bg-yellow-50 border-yellow-200",
  };

  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border ${bgColors[toast.type]} animate-slide-in-right min-w-[280px] max-w-md`}
    >
      <span className="text-lg">{icons[toast.type]}</span>
      <span className="flex-1 text-sm text-gray-700">{toast.message}</span>
      <button
        onClick={onClose}
        className="text-gray-400 hover:text-gray-600 transition-colors"
      >
        <FaTimes />
      </button>
    </div>
  );
}

export default ToastProvider;
